import React, { useEffect, useState } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";

// Componentes de diseño
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import DynamicTable from "../../../components/listing/DynamicTable"; // <--- IMPORTANTE

// Componentes locales
import FiltroBonificacion from "./FiltroBonificacion";
import CrearBonificacion from "./CrearBonificacion";
import EditarBonificacion from "./EditarBonificacion";
import EliminarBonificacion from "./EliminarBonificacion";

// API & Utils
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

export default function ListarBonificacion() {
  // ================================
  //   PAGINACIÓN & ESTADO
  // ================================
  const [porPagina, setPorPagina] = useState(10);
  const [pagina, setPagina] = useState(1);
  const [totalBackend, setTotalBackend] = useState(0);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [mostrarCrear, setMostrarCrear] = useState(false);
  
  // Acciones
  const [registroAEditar, setRegistroAEditar] = useState(null);
  const [registroAEliminar, setRegistroAEliminar] = useState(null);

  // Filtros
  const [filtros, setFiltros] = useState({
    nombre: "",
    rubroId: "", 
    eessId: "",  
  });
  
  const [reloadKey, setReloadKey] = useState(0);

  // ================================
  //   CARGA DE DATOS
  // ================================
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        let baseUrl = apiRoutes.bonificacion?.listUrl 
            ? apiRoutes.bonificacion.listUrl({}) 
            : (apiRoutes.bonificacion?.list || ""); 

        if (!baseUrl) {
             console.error("Falta definir apiRoutes.bonificacion.list");
             return;
        }
        
        if (baseUrl.includes('?')) {
            baseUrl = baseUrl.split('?')[0];
        }

        const queryParams = new URLSearchParams();
        queryParams.append("page", pagina - 1);
        queryParams.append("size", porPagina);
        
        if (filtros.nombre) queryParams.append("nombre", filtros.nombre.trim());
        if (filtros.rubroId) queryParams.append("rubroId", filtros.rubroId);
        if (filtros.eessId) queryParams.append("eessId", filtros.eessId);

        const url = `${baseUrl}?${queryParams.toString()}`;
        const res = await appFetchJson(url);

        const items = Array.isArray(res?.items) ? res.items : [];
        setData(items);

        const total = res?.totalElements ?? res?.total ?? (Array.isArray(items) ? items.length : 0);
        setTotalBackend(total);

      } catch (e) {
        console.error("Error cargando bonificaciones:", e);
        setError("Error al cargar las bonificaciones.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pagina, porPagina, filtros, reloadKey]);

  // ====================================================================
  // DEFINICIÓN DE COLUMNAS (TOTAL 100%)
  // ====================================================================
  const columns = [
    // 1. DESCRIPCIÓN (30%) - Dato principal
    { 
      header: "Descripción", 
      accessor: "nombre",
      type: "text", 
      width: "30%", 
      cellClass: "whitespace-normal break-words leading-tight font-medium text-gray-800"
    },

    /*const columns = [
    // 1. DESCRIPCIÓN (30%) - Dato principal
    { 
      header: "Descripción", 
      accessor: "nombre",
      type: "text", 
      width: "30%", 
      cellClass: "whitespace-normal break-words leading-tight font-medium",
      render: (row) => (
        <span className="text-green-600 lg:text-gray-800 font-bold lg:font-medium">
          {row.nombre}
        </span>
      )
    },*/

    // 2. CANTIDAD (15%) - Numérico centrado
    { 
      header: "Cantidad", 
      type: "center", 
      width: "15%", 
      render: (row) => row.cantidad != null 
        ? Number(row.cantidad).toLocaleString("es-PY", { minimumFractionDigits: 1 }) 
        : "-"
    },

    // 3. EE.SS. (25%) - Texto secundario importante
    { 
      header: "EE.SS.", 
      type: "text", 
      width: "25%",
      cellClass: "text-gray-600 whitespace-normal break-words leading-tight",
      render: (row) => row.eessNombre || "-"
    },

    // 4. RUBRO (20%) - Informativo
    { 
      header: "Rubro", 
      type: "text", 
      width: "20%",
      cellClass: "text-gray-600 whitespace-normal break-words leading-tight",
      render: (row) => row.rubroNombre || "-"
    },

    // 5. ACCIÓN (10%)
    {
      header: "Acción",
      type: "action", 
      width: "10%",
      render: (it) => (
        <div className="flex justify-center items-center gap-1">
          <button 
            className="text-gray-500 hover:text-blue-600 p-1" 
            title="Editar"
            onClick={() => setRegistroAEditar(it)}
          >
            <FaEdit />
          </button>
          <button 
            className="text-gray-500 hover:text-red-600 p-1" 
            title="Eliminar"
            onClick={() => setRegistroAEliminar(it)}
          >
            <FaTrashAlt />
          </button>
        </div>
      )
    }
  ];

  // ================================
  //   RENDER
  // ================================
  return (
    <div className="page">

      <ListHeader
        title="Bonificaciones"
        filters={filtros}
        onFilterClick={() => setMostrarFiltros(true)}
        onClearFilters={() => {
          setFiltros({ nombre: "" });
          setPagina(1);
        }}
        showAdd={true}
        addLabel="Agregar"
        onAddClick={() => setMostrarCrear(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPagina(1);
        }}
      />

      {loading && (
        <div className="mt-2 text-sm text-center text-gray-600">Cargando datos...</div>
      )}
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}

      {/* DYNAMIC TABLE */}
      {!loading && (
        <DynamicTable 
          size="M" // 5 columnas -> Se ve bien en 'M' (cards < 1024px)
          data={data}
          columns={columns}
          // No pasamos getRowColor porque Bonificación no parece tener un estado Activo/Inactivo
        />
      )}

      <ListFooter
        page={pagina}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={(p) => setPagina(p)}
      />

      {/* --- MODALES --- */}
      
      {/* Drawer filtros */}
      {mostrarFiltros && (
        <FiltroBonificacion
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            setPagina(1);
            setMostrarFiltros(false);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* Crear */}
      {mostrarCrear && (
        <CrearBonificacion
          onClose={() => setMostrarCrear(false)}
          onCreated={() => {
            setReloadKey((k) => k + 1);
            setMostrarCrear(false);
          }}
        />
      )}

      {/* Editar */}
      {registroAEditar && (
        <EditarBonificacion
          record={registroAEditar}
          onClose={() => setRegistroAEditar(null)}
          onUpdated={() => {
            setReloadKey((k) => k + 1);
            setRegistroAEditar(null);
          }}
        />
      )}

      {/* Eliminar */}
      {registroAEliminar && (
        <div className="modal-backdrop" onClick={() => setRegistroAEliminar(null)}>
          <div className="modal-panel-md" onClick={(e) => e.stopPropagation()}>
            <EliminarBonificacion
              registro={registroAEliminar}
              onClose={() => setRegistroAEliminar(null)}
              onDeleted={() => {
                setReloadKey((k) => k + 1);
                setRegistroAEliminar(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}